import { Bank, HubConfig, LandingPagesInfos, ProfileAdditionalInfo } from '@/hub-shared'
import { BankResponse } from '@/interfaces/bank.interface'
import { Cache } from '@/interfaces/user.interface'
import Category from '@interfaces/category.interface'
import type Environment from '@interfaces/environment.interface'
import onEnvironmentChange from '@observables/environment.observable'
import LogService from '@services/log/log.service'
import MainService from '@services/main/main.service'
import type { AxiosError, AxiosResponse } from 'axios'
import { take } from 'rxjs/operators'

interface InfluencerStatus {
  status: boolean
}

export default class DakenService extends MainService {
  url: string
  logService = new LogService()

  constructor(dakenUrl?: string) {
    super()
    if (!dakenUrl) {
      onEnvironmentChange.pipe(take(1)).subscribe((environment: Environment) => {
        this.url = environment?.squidApis?.daken
      })
    } else {
      this.url = dakenUrl
    }
    this.logService.init('Daken Service')
  }

  public async getInfluencersInfo(JWT: string = null) {
    const headers = await this.getHeaders(true, JWT)
    return this.http
      .get(`${this.url}/influencers/info`, { headers })
      .then((r: AxiosResponse) => r?.data || {})
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Can not get influencers info',
          error,
        })
        throw error.response
      })
  }

  public async getInflucencersCompleteness(
    profileId: string,
    type: 'profile' | 'register' | 'profile-mediakit' | 'profile-search' | 'progressive' = 'register'
  ) {
    if (!profileId) {
      throw new Error('profileId required')
    }
    const headers = await this.getHeaders(true)
    return this.http
      .get(`${this.url}/influencers/completeness/${type}/${profileId}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Can not get influencers completeness',
          error,
        })
        return error.response
      })
  }

  public async patchInflucencersNotificationsConfig(
    profileId: string,
    field: 'allowSMS' | 'allowWhatsapp' | 'allowSuggestionEmail' | 'allowBrandLovers' | 'allowAffiliates' | 'allowResearch'
  ) {
    if (!profileId) {
      throw new Error('profileId required')
    }
    if (!field) {
      throw new Error('field required')
    }
    const headers = await this.getHeaders(true)
    return this.http
      .patch(`${this.url}/influencers/notification`, { profileId, field }, { headers })
      .then(() => true)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Can not update influencers notifications config',
          error,
        })
        return false
      })
  }

  public async getCountUserField(field: string, value: string, validAllowed = false): Promise<any> {
    const headers = await this.getHeaders(false, '', false)
    return this.http
      .get(`${this.url}/profile-additional-infos/count/${field}/${value}${validAllowed ? '?validAllowed=true' : ''}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Count failed', error })
        throw error.response.data
      })
  }

  public async getCheckDocumentWithCountry(document: string, country: string, validAllowed = false): Promise<any> {
    const headers = await this.getHeaders(false, '', false)
    if (!document) throw new Error('É necessário um documento válido para fazer a pesquisa')
    const paramCountry = country ? `nationality=${country}` : ''
    const paramDocument = document ? `document=${document}` : ''
    return this.http
      .get(`${this.url}/profile-additional-infos/document?${paramDocument}&${paramCountry}${validAllowed ? '&validAllowed=true' : ''}`, {
        headers,
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Failed to check document', error: error })
        throw error.response.data
      })
  }

  public async insertAdditionalInfo(info: ProfileAdditionalInfo): Promise<ProfileAdditionalInfo> {
    if (!info) throw new Error('The information object to be entered is required')

    const headers = await this.getHeaders(false, '', false)

    return this.http
      .post(`${this.url}/hub/profile-additional-infos/self`, info, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Failed to enter additional information', error: error })
        throw error.response
      })
  }

  public async updateAdditionalInfo(profile: Partial<ProfileAdditionalInfo>): Promise<ProfileAdditionalInfo> {
    if (!profile) {
      throw new Error('Profile required')
    }
    const headers = await this.getHeaders()
    return this.http
      .put(`${this.url}/hub/profile-additional-infos/self`, { profile }, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Fail on profile edit', error: error })
        throw error.response
      })
  }

  public async resendEmailAuth0(email: string): Promise<InfluencerStatus> {
    const headers = await this.getHeaders(false, '', false)
    return this.http
      .post(`${this.url}/hub/resend-email-validation`, { email }, { headers })
      .then((r: AxiosResponse) => r?.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Failed to resend email validation',
          error,
        })
        throw error.response
      })
  }

  public async deleteProfileAccount(squidId: string, deletionMotivationEncoded: string, deleteAll = 'false') {
    const headers = await this.getHeaders()

    const URL = `${this.url}/profile/${squidId}/motivation/${deletionMotivationEncoded}?deleteAll=${deleteAll}`
    return this.http
      .delete(URL, {
        headers,
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Failed to delete social network', error })
        throw error.response
      })
  }

  public async getCategoriesBySocialNetwork(profileId: string): Promise<Category[]> {
    if (!profileId) {
      throw new Error('Social network required')
    }
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/social-network-profiles-categories/${profileId}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Failed to get categories by social network', error })
        throw error.response
      })
  }

  public async putProfileCategories(profileId: string, payload: { categories: number[] }): Promise<Category> {
    if (!profileId) {
      throw new Error('profileId required')
    }
    if (!payload) {
      throw new Error('payload required')
    }
    const headers = await this.getHeaders()
    return this.http
      .put(`${this.url}/social-network-profiles-categories/${profileId}`, { ...payload }, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Failed to save by social network', error })
        throw error.response
      })
  }

  public async getBanksFieldCount(document: string, field = 'recordEmployment') {
    if (!document) {
      throw new Error('Document required')
    }
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/banks/count?field=${field}&value=${document}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Failed to verify recordEmployment', error })
        throw error.response
      })
  }

  async postAddBankInfo(payload: Partial<Bank>): Promise<BankResponse> {
    const headers = await this.getHeaders()
    return this.http
      .post(`${this.url}/banks`, payload, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Failed to insert bank info', error })
        throw error.response
      })
  }

  async patchUpdateBankInfo(bankId: number, payload: Partial<Bank>): Promise<BankResponse> {
    if (!bankId) {
      throw new Error('bankId required')
    }
    const headers = await this.getHeaders()
    return this.http
      .patch(`${this.url}/banks/${bankId}`, payload, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Failed to update bank info', error })
        throw error.response
      })
  }

  public async getInfluencerHasWhitelabelAccess(email: string): Promise<InfluencerStatus> {
    const headers = await this.getHeaders(false, '', false)
    if (!email) {
      throw new Error('email required')
    }
    return this.http
      .get(`${this.url}/influencers/profile-whitelabel/access/${email}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Failed to get information if email exists in whitelabel',
          error,
        })
        throw error.response
      })
  }

  public async getProfileWhiteLabel(email?: string, profileId?: string): Promise<LandingPagesInfos> {
    if (!email && !profileId) {
      throw new Error('email and profileId required')
    }
    const query = new URLSearchParams()
    if (email) {
      query.append('email', email)
    }
    if (profileId) {
      query.append('profileId', profileId)
    }
    return this.http
      .get(`${this.url}/profile-whitelabel?${query.toString()}`)
      .then((r: AxiosResponse) => r?.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Failed to get whitelabel information', error: error })
        throw error.response
      })
  }

  public async putProfileCache({
    socialId,
    cache,
  }: {
    socialId: string
    cache: { posts: number | null; stories: number | null; reels: number | null }
  }): Promise<Cache[]> {
    if (!socialId) {
      throw new Error('socialId required')
    }

    const headers = await this.getHeaders()
    return this.http
      .put(`${this.url}/influencer/social-network-cache/${socialId}`, { ...cache }, { headers })
      .then((r: AxiosResponse) => r?.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Failed to update profile cache', error })
        throw error?.response
      })
  }

  async postBindWhitelabel({
    profileId,
    registeredFromMedium = null,
    registeredFromSource = 'hub',
    registeredFromCampaign = null,
    registeredFromContent = null,
    registeredFromAdId = null,
    registeredFromAdName = null,
  }): Promise<HubConfig> {
    const headers = await this.getHeaders(false, '', false)
    return this.http
      .post(
        `${this.url}/profile-whitelabel/bind`,
        {
          profileId,
          registeredFromMedium,
          registeredFromSource,
          registeredFromCampaign,
          registeredFromContent,
          registeredFromAdId,
          registeredFromAdName,
        },
        { headers }
      )
      .then((r: AxiosResponse) => r?.data)
      .catch((error: AxiosError) => {
        this.logService.error({ message: 'Failed to link with LP', error: error })
        throw error.response
      })
  }

  public async postPhoneValidationCode(channel: 'whatsapp' | 'sms') {
    const headers = await this.getHeaders(true)
    const payload = { channel }

    return this.http
      .post(`${this.url}/influencers/phone/validation`, payload, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Cannot send phone validation code',
          error,
        })
        return error.response?.data
      })
  }

  public async putPhoneVerifyCode(code: string) {
    const headers = await this.getHeaders(true)
    const payload = { code }

    return this.http
      .put(`${this.url}/influencers/phone/validation`, payload, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Cannot verify phone code',
          error,
        })
        return error.response?.data
      })
  }
}
